import React, { useEffect, useState, useRef } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";

// --- Interfaces ---

interface Campaign {
  id: string;
  name: string;
  date: string;
  subject?: string;
  previewText?: string;
  htmlContent?: string;
  status: string;
  delivered: string | number;
  openRate: string;
  clickRate: string;
  unsubs: string;
  fromFieldId?: string;
  campaignId?: string;  // Main list ID for this campaign
  includedSegmentIds?: string[];
  excludedSegmentIds?: string[];
  sendSettings?: {
    selectedSegments?: string[];
    excludedSegments?: string[];
    selectedCampaigns?: string[];  // Included lists
    excludedCampaigns?: string[];  // Excluded lists
    genderSegment?: string[];      // Feedblitz M/F/U gender segment values
  };
}

// --- Constants ---
const systems = [
  { id: "gr_se", label: "GetResponse (SE)", country: "SE", esp: "getresponse" },
  { id: "gr_se2", label: "GetResponse (SE 2)", country: "SE", esp: "getresponse" },
  { id: "gr_fi", label: "GetResponse (FI)", country: "FI", esp: "getresponse" },
  { id: "gr_de", label: "GetResponse (DE)", country: "DE", esp: "getresponse" },
  { id: "gr_no", label: "GetResponse (NO)", country: "NO", esp: "getresponse" },
  { id: "br_fi", label: "Brevo (FI)", country: "FI", esp: "brevo" },
  { id: "br_sl_se", label: "Brevo SimplyLoan (SE)", country: "SE", esp: "brevo" },
  { id: "br_sl_fi", label: "Brevo SimplyLoan (FI)", country: "FI", esp: "brevo" },
  { id: "br_sl_no", label: "Brevo SimplyLoan (NO)", country: "NO", esp: "brevo" },
  { id: "feedblitz_fi", label: "Feedblitz (FI)", country: "FI", esp: "feedblitz" },
  { id: "feedblitz_de", label: "Feedblitz (DE)", country: "DE", esp: "feedblitz" },
  { id: "feedblitz_no", label: "Feedblitz (NO)", country: "NO", esp: "feedblitz" },
  { id: "feedblitz_se", label: "Feedblitz (SE)", country: "SE", esp: "feedblitz" },
  { id: "moosend_se", label: "Moosend (SE)", country: "SE", esp: "moosend" },
  { id: "mailerlite_fi", label: "MailerLite (FI)", country: "FI", esp: "mailerlite" }
];

const countries = [
  { id: "SE", label: "Sweden" },
  { id: "FI", label: "Finland" },
  { id: "NO", label: "Norway" },
  { id: "DE", label: "Germany" }
];

function formatDateCopenhagen(dateStr: string) {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Copenhagen",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Split formatted string and return "DD-MM HH:MM"
    const parts = formatter.formatToParts(date);
    const day = parts.find(p => p.type === "day")?.value;
    const month = parts.find(p => p.type === "month")?.value;
    const hour = parts.find(p => p.type === "hour")?.value;
    const minute = parts.find(p => p.type === "minute")?.value;

    return `${day}-${month} ${hour}:${minute}`;
  } catch {
    return dateStr;
  }
}

/**
 * Pure String Manipulation Gatekeeper for SimplyLoan
 * Bypasses the DOMParser to prevent browser HTML escaping bugs (e.g. &amp;s3=)
 */
function scrubSimplyLoanLinks(htmlContent: string, espId: string, afimaCampaignId: string): string {
  if (!htmlContent) return '';
  
  // Extract domain extension (se, no, fi) from espId e.g. "br_sl_se"
  const domainExtMatch = espId.match(/br_sl_([a-z]{2})/);
  if (!domainExtMatch) return htmlContent; // Only run for br_sl_*
  
  const domainExt = domainExtMatch[1];
  const s1_outbound = `sl${domainExt}em`; // slseem, slnoem, slfiem
  const s1_inbound  = `sl${domainExt}ew`; // slseew, slnoew, slfiew

  let finalHtml = htmlContent;

  // 1. Upgrade legacy /recommends/ to /slgo/
  // Match href="https://simplyloan.se/recommends/lendo?..."
  const upgradeRegex = new RegExp(`href="https?://(?:www\\.)?simplyloan\\.${domainExt}/(?:recommends|redirects|slgo)/([^/"?]+)[^"]*"`, 'gi');
  finalHtml = finalHtml.replace(upgradeRegex, (match, slug) => {
    return `href="https://simplyloan.${domainExt}/slgo/${slug}/?s1=${s1_outbound}&s2={{contact.CID}}&s3=${afimaCampaignId}&s4=email_direct&s5={{contact.UTM_SOURCE}}"`;
  });

  // 2. Scrub ALL simplyloan internal links (Pincetten)
  // Match href="https://simplyloan.se/..."
  const internalRegex = new RegExp(`href="(https?://(?:www\\.)?simplyloan\\.${domainExt}/[^"]*)"`, 'gi');
  finalHtml = finalHtml.replace(internalRegex, (match, urlStr) => {
    // Skip if it's already an upgraded /slgo/ link (we just did that above)
    if (urlStr.includes('/slgo/')) return match;
    
    try {
      // Decode HTML entities if the browser DOM parser accidentally added them earlier
      let cleanUrlStr = urlStr.replace(/&amp;/g, '&');
      const url = new URL(cleanUrlStr);
      
      const survivingParams: string[] = [];
      url.searchParams.forEach((value, key) => {
        const k = key.toLowerCase();
        if (!['s1', 's2', 's3', 's4', 's5'].includes(k) && 
            !k.startsWith('utm_') && 
            !k.startsWith('pubidsub') && 
            !k.startsWith('epi') && 
            !k.startsWith('subid') && 
            !k.startsWith('aff_sub')) {
          survivingParams.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
      
      const sParams = [
        `s1=${s1_inbound}`,
        `s2={{contact.CID}}`,
        `s3=${afimaCampaignId}`,
        `s4=email_${s1_inbound}`,
        `s5={{contact.UTM_SOURCE}}`
      ];
      
      const newQuery = [...sParams, ...survivingParams].join('&');
      return `href="${url.origin}${url.pathname}?${newQuery}"`;
    } catch(e) {
      return match;
    }
  });

  return finalHtml;
}

/** Pre-flight checker algorithm to validate and inject tracking params */
function validateAndInjectTracking(htmlContent: string, espId: string, leadPlaceholder: string, textVersion: string, sendId: string, expectedDomains: string[]): string {
  if (!htmlContent) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const links = doc.querySelectorAll('a');

  let invalidLinks: string[] = [];

  links.forEach(link => {
    let href = link.getAttribute('href');
    if (!href) return;
    href = href.trim();
    
    // Ignore non-navigational standard tags
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href === '#') return;
    
    // Ignore typical ESP unsubscribe tags, but DO NOT ignore {{LINK...}} syntax
    if ((href.startsWith('{{') || href.startsWith('[[') || href.startsWith('{$')) && !href.startsWith('{{LINK')) return;

    // Ignore Moosend system macros for webversion and unsubscribe
    if (href.startsWith('#unsubscribe') || href.includes('#trackingDomain#') || href.includes('#campaign:key#')) return;

    let targetUrl = href;
    let isLinkTemplate = false;
    let templateId = '';

    // Handle special Afima {{LINK `url` `id`}} syntax
    const linkMatch = href.match(/^{{LINK\s+`([^`]+)`\s+`([^`]+)`}}$/);
    if (linkMatch) {
      isLinkTemplate = true;
      targetUrl = linkMatch[1];
      templateId = linkMatch[2];
    }

    // Must use one of the authorized /go/ domains
    const isAuthorized = expectedDomains.some(d => {
      if (d.includes('simplyloan')) {
        return targetUrl.includes(d); // Gatekeeper will rewrite the path
      }
      return targetUrl.includes(d + '/go/');
    });
    if (!isAuthorized) {
      invalidLinks.push(targetUrl);
      return;
    }

    try {
      const urlString = targetUrl.startsWith('http') ? targetUrl : `https://${targetUrl}`;
      const url = new URL(urlString);
      
      // Auto-inject sub-id tags ONLY for non-SimplyLoan ESPs (SimplyLoan Gatekeeper handles its own tracking in the backend)
      url.hash = ''; // Clear native HTML anchors so they don't incorrectly map themselves onto the end of new parameters!
      if (!espId.startsWith('br_sl_')) {
        url.searchParams.set('s2', "___AFIMA_LEAD___");
        url.searchParams.set('s3', textVersion);
        url.searchParams.set('s4', sendId);
      }

      // Reconstruct the href
      if (isLinkTemplate) {
        link.setAttribute('href', `{{LINK \`${url.toString()}\` \`${templateId}\`}}`);
      } else {
        link.setAttribute('href', url.toString());
      }
    } catch (e) {
      invalidLinks.push(href);
    }
  });

  if (invalidLinks.length > 0) {
    throw new Error(`All links must use ${expectedDomains.join(' or ')}/go/.\nInvalid links:\n${invalidLinks.slice(0, 3).join('\n')}`);
  }

  // Some DOM parsers decode HTML entities in href attributes which breaks template tags.
  // We return the outerHTML and gently fix the {{LINK...}} backticks if the browser escaped them.
  let finalHtml = doc.documentElement.outerHTML;
  finalHtml = finalHtml.replace(/\{\{LINK %60(.*?)%60 %60(.*?)%60\}\}/g, '{{LINK `$1` `$2`}}');
  
  // Re-inject the raw un-encoded ESP shortcode bypassing all URL security encoding so the ESP detects it cleanly
  finalHtml = finalHtml.replace(/___AFIMA_LEAD___/g, leadPlaceholder);
  
  return finalHtml;
}

export default function CampaignDashboard() {
  // Helper to update the scheduled time input fields from an ISO string
  function updateScheduledTimeText(isoString: string) {
    try {
      const date = new Date(isoString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");

      setScheduledDay(day);
      setScheduledMonth(month);
      setScheduledHour(hour);
      setScheduledMinute(minute);
    } catch {
      setScheduledDay("");
      setScheduledMonth("");
      setScheduledHour("");
      setScheduledMinute("");
    }
  }

  // Tries to construct an ISO datetime (UTC) string from the day, month, hour, minute parts.
  function trySetScheduledTimeFromParts(day: string, month: string, hour: string, minute: string) {
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const h = parseInt(hour, 10);
    const min = parseInt(minute, 10);

    if (
      !isNaN(d) && d >= 1 && d <= 31 &&
      !isNaN(m) && m >= 1 && m <= 12 &&
      !isNaN(h) && h >= 0 && h <= 23 &&
      !isNaN(min) && min >= 0 && min <= 59
    ) {
      const year = new Date().getFullYear();
      // Convert local time (UTC+2 offset assumed) to a UTC ISO string
      const localDate = new Date(year, m - 1, d, h, min); // ✅ Local time (user input)
      setScheduledTime(localDate.toISOString()); // Converts to UTC
    } else {
      // If any part is invalid, clear the scheduled time
      setScheduledTime("");
    }
  }

  // --- State ---
  const [selectedCountry, setSelectedCountry] = useState("SE");
  const [selectedEsp, setSelectedEsp] = useState("");
  const [tab, setTab] = useState("SE");
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [lastUpdated, setLastUpdated] = useState("2024.06.20");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [lists, setLists] = useState<any[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [htmlBody, setHtmlBody] = useState("");
  const [htmlEditorOpen, setHtmlEditorOpen] = useState(false);
  const [htmlEditorExpanded, setHtmlEditorExpanded] = useState(false);
  const [senders, setSenders] = useState<any[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<string>("");
  const [selectedListId, setSelectedListId] = useState<string>("");
  const [selectedIncludedListIds, setSelectedIncludedListIds] = useState<string[]>([]);
  const [selectedExcludedListIds, setSelectedExcludedListIds] = useState<string[]>([]);
  const [listDropdownValue, setListDropdownValue] = useState<string>("");
  const [selectedIncludedSegmentIds, setSelectedIncludedSegmentIds] = useState<string[]>([]);
  const [selectedExcludedSegmentIds, setSelectedExcludedSegmentIds] = useState<string[]>([]);
  const [selectedGenderValues, setSelectedGenderValues] = useState<string[]>([]);  // Feedblitz M/F/U gender segment
  const [scheduleQueue, setScheduleQueue] = useState<Campaign[]>([]);
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [scheduling, setScheduling] = useState(false);
  const [campaignsToDelete, setCampaignsToDelete] = useState<Campaign[]>([]);
  const [deletedCampaigns, setDeletedCampaigns] = useState<Campaign[]>([]);
  const [updatingData, setUpdatingData] = useState(false);
  const [deletingCampaigns, setDeletingCampaigns] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [scheduledDay, setScheduledDay] = useState("");
  const [scheduledMonth, setScheduledMonth] = useState("");
  const [scheduledHour, setScheduledHour] = useState("");
  const [scheduledMinute, setScheduledMinute] = useState("");
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const [refreshingAfterSchedule, setRefreshingAfterSchedule] = useState(false);

  // Handler for when the user finishes editing the date/time fields (on blur)
  const handleScheduledTimeBlur = () => {
    const datePart = `${scheduledDay}/${scheduledMonth}`;
    const timePart = `${scheduledHour}:${scheduledMinute}`;
    if (!datePart || !timePart) return;

    const [day, month] = datePart.split("/").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);

    if (
      !Number.isFinite(day) || !Number.isFinite(month) ||
      !Number.isFinite(hour) || !Number.isFinite(minute)
    ) return;

    const now = new Date();
    const currentYear = now.getFullYear();

    // First assume current year
    let date = new Date(currentYear, month - 1, day, hour, minute);

    // If that date/time is in the past, roll over to next year
    if (date.getTime() < now.getTime()) {
      date = new Date(currentYear + 1, month - 1, day, hour, minute);
    }

    if (!isNaN(date.getTime())) {
      setScheduledTime(date.toISOString().slice(0, 16));
    }
  };



  // Schedules all campaigns in the queue by calling the API and then refreshing data
  const handleScheduleAll = async () => {
    const todayStr = new Date().toISOString().slice(0, 10);

    if (scheduleQueue.length === 0) {
      window.alert("No campaigns to schedule.");
      return;
    }

    setScheduling(true);
    setRefreshingAfterSchedule(true);

    try {
      const res = await fetch(
        "https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/schedule-campaigns",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            systemId: selectedEsp,
            campaigns: scheduleQueue
          }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setScheduleQueue([]);

        const selectedSystem = systems.find(s => s.id === selectedEsp);
        if (selectedSystem) {
          const params = new URLSearchParams({
            esp: selectedSystem.esp,
            account_id: selectedSystem.id,
            from_date: todayStr,
            to_date: todayStr
          });

          // Trigger an update for today's data after scheduling
          await fetch(
            `https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/update-all?${params.toString()}`,
            { method: "POST" }
          );

          await fetchCampaignDataDashboard();
        }
      } else {
        console.error("Schedule failed", data);
        window.alert("Error scheduling campaigns: " + (data?.error || res.status));
      }
    } catch (err) {
      console.error("Network error during scheduling:", err);
      window.alert("Network error: " + err);
    } finally {
      setScheduling(false);
      setRefreshingAfterSchedule(false);
    }
  };

  // Toggle a campaign in the deletion list (unused in current UI but could be used later)
  const toggleDeleteCampaign = (c: Campaign) => {
    setCampaignsToDelete(current =>
      current.some(x => x.id === c.id)
        ? current.filter(x => x.id !== c.id)
        : [...current, c]
    );
  };

  // Segment options (if needed for filtering, though not fully used in UI)
  const allSegments = [
    { value: "all", label: "All users" },
    { value: "new", label: "New leads" },
    { value: "vip", label: "VIPs" },
    { value: "unsub", label: "Unsubscribed" },
    { value: "bounced", label: "Bounced" },
    { value: "inactive", label: "Inactive" }
  ];
  const [includeSegments, setIncludeSegments] = useState<string[]>([]);
  const [excludeSegments, setExcludeSegments] = useState<string[]>([]);

  // HTML preview height
  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [previewHeight, setPreviewHeight] = useState(384);

  // Fetch campaign data for the selected ESP account
  const fetchCampaignDataDashboard = async () => {
    const apiUrl = `https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/fetchCampaignData?accountId=${selectedEsp}`;
    try {
      const res = await fetch(apiUrl);
      const data = await res.json();
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
      setLists(Array.isArray(data.lists) ? data.lists : []);
      setSegments(Array.isArray(data.segments) ? data.segments : []);
      setSenders(Array.isArray(data.senders) ? data.senders : []);
    } catch (err) {
      console.error("Fetch failed:", err);
      setCampaigns([]);
    }
  };

  // --- useEffect: Fetch campaigns on ESP or date filter change ---
  useEffect(() => {
    if (!selectedEsp) {
      setCampaigns([]);
      return;
    }
    setLoading(true);
    const apiUrl = `https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/fetchCampaignData?accountId=${selectedEsp}`;
    console.log("Fetching campaigns from:", apiUrl);
    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
        setLists(Array.isArray(data.lists) ? data.lists : []);
        setSegments(Array.isArray(data.segments) ? data.segments : []);
        setSenders(Array.isArray(data.senders) ? data.senders : []);
        setSelectedSenderId(""); // Reset selected sender on ESP change
      })
      .catch(err => {
        setCampaigns([]);
        console.error("Fetch failed:", err);
        // Optionally, handle error state
      })
      .finally(() => setLoading(false));
  }, [selectedEsp]);

  // --- useEffect: Adjust preview pane height when HTML textarea is resized ---
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (htmlTextareaRef.current) {
        setPreviewHeight(htmlTextareaRef.current.offsetHeight);
      }
    });
    if (htmlTextareaRef.current) observer.observe(htmlTextareaRef.current);
    return () => observer.disconnect();
  }, []);

  // --- useEffect: Reset form fields when country changes ---
  useEffect(() => {
    setSelectedEsp("");
    setCampaigns([]);
    resetEditCampaignBox();
    setSegments([]);
    setSenders([]);
    setLists([]);
    setIncludeSegments([]);
    setExcludeSegments([]);
  }, [selectedCountry]);

  // Handle changing selected country (resets some UI)
  const handleCountryChange = (value: string) => {
    setLoading(true);
    setSelectedCountry(value);
    setTab(value);
    setTimeout(() => setLoading(false), 300);
  };

  // Dummy handler for "Get Data" button (can be expanded to use fromDate/toDate)
  const handleGetData = () => {
    alert(`Get Data for ${selectedEsp} from ${fromDate} to ${toDate}`);
  };

  // Handle the "Update all data" button to refresh campaign data from server
  const handleUpdateAllData = async () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const from = fromDate || todayStr;
    const to = toDate || todayStr;

    const selectedSystem = systems.find(s => s.id === selectedEsp);
    if (!selectedSystem) {
      alert("No ESP selected");
      return;
    }

    const params = new URLSearchParams({
      esp: selectedSystem.esp,
      account_id: selectedSystem.id,
      from_date: from,
      to_date: to
    });

    setUpdatingData(true);
    setCampaigns([]); // clear table while updating
    setLastUpdated(todayStr);

    try {
      const res = await fetch(
        `https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/update-all?${params.toString()}`,
        { method: "POST" }
      );
      const json = await res.json();

      if (res.ok) {
        console.log("✅ Update successful", json);
        await fetchCampaignDataDashboard(); // re-fetch updated campaigns
      } else {
        console.error("❌ Update failed", json);
        alert("Update failed: " + (json?.error || res.status));
      }
    } catch (err) {
      console.error("❌ Network error", err);
      alert("Network error: " + err);
    } finally {
      setUpdatingData(false);
    }
  };

  // Control for selecting a list to include/exclude (not currently used in UI, since we have main list selector)
  const listSelectControl = (
    <select
      className="flex-grow border rounded px-2 py-1 text-sm"
      value={listDropdownValue}
      onChange={e => setListDropdownValue(e.target.value)}
    >
      <option value="">Select list to include/exclude</option>
      {lists.map(list => (
        <option key={list.campaignId} value={list.campaignId}>
          {list.name}
        </option>
      ))}
    </select>
  );

  // Include button for additional lists (if needed)
  const includeButton = (
    <button
      type="button"
      className="px-2 py-1 rounded bg-blue-500 text-white text-xs"
      disabled={!listDropdownValue || selectedIncludedListIds.includes(listDropdownValue)}
      onClick={() => {
        setSelectedIncludedListIds(ids =>
          ids.includes(listDropdownValue) ? ids : [...ids, listDropdownValue]
        );
        setListDropdownValue("");
      }}
    >
      Include
    </button>
  );

  // Exclude button for additional lists (if needed)
  const excludeButton = (
    <button
      type="button"
      className="px-2 py-1 rounded bg-red-500 text-white text-xs"
      disabled={!listDropdownValue || selectedExcludedListIds.includes(listDropdownValue)}
      onClick={() => {
        setSelectedExcludedListIds(ids =>
          ids.includes(listDropdownValue) ? ids : [...ids, listDropdownValue]
        );
        setListDropdownValue("");
      }}
    >
      Exclude
    </button>
  );

  // Handle selecting a campaign from the table to edit
  const handleCampaignClick = (campaign: Campaign) => {
    console.log("📦 Segments:", segments);
    console.log("Senders:", senders)
    console.log("Lists:", lists)
    console.log("🧾 Clicked campaign:", campaign);
    console.log("Status:", campaign.status)
    const ss = campaign.sendSettings ?? {};

    setSelectedCampaign(campaign);
    setHtmlBody(campaign.htmlContent || "");
    setSelectedSenderId(campaign.fromFieldId || "");
    setScheduledTime("");

    // Set the main list (dropdown) for the campaign
    setSelectedListId(campaign.campaignId || "");

    // Included and excluded lists
    const includedLists = Array.isArray(ss.selectedCampaigns) ? ss.selectedCampaigns : [];
    const excludedLists = Array.isArray(ss.excludedCampaigns) ? ss.excludedCampaigns : [];
    setSelectedIncludedListIds(includedLists);
    setSelectedExcludedListIds(excludedLists);

    // Included and excluded segments
    const rawIncSeg = Array.isArray(campaign.includedSegmentIds) ? campaign.includedSegmentIds : [];
    const rawExcSeg = Array.isArray(campaign.excludedSegmentIds) ? campaign.excludedSegmentIds : [];
    
    // Automatically upgrade pure legacy segment IDs into compound formats (ListID_SegmentID) 
    // so the HTML <select> recognizes them as natively checked values!
    const activeSystem = systems.find(s => s.id === selectedEsp);
    const upgradeSegment = (segId: string | number) => {
      const targetId = String(segId);
      if (!activeSystem || activeSystem.esp !== "moosend") return targetId;
      if (targetId.includes('_')) return targetId;
      const matched = segments.find(s => String(s.searchContactId).endsWith(`_${targetId}`));
      return matched ? String(matched.searchContactId) : targetId;
    };

    const updatedExcSegs = rawExcSeg.map(upgradeSegment);
    
    // Auto-select Ru_spam_exclusion for SimplyLoan campaigns
    if (activeSystem?.id.startsWith("br_sl_")) {
      const spamSeg = segments.find(s => s.name === "Ru_spam_exclusion");
      if (spamSeg && !updatedExcSegs.includes(String(spamSeg.searchContactId))) {
        updatedExcSegs.push(String(spamSeg.searchContactId));
      }
    }

    setSelectedIncludedSegmentIds(rawIncSeg.map(upgradeSegment));
    setSelectedExcludedSegmentIds(updatedExcSegs);
  };

  // Handle deletion of scheduled campaigns via API
  const handleDeleteScheduledCampaigns = async () => {
    if (deletedCampaigns.length === 0 || !selectedEsp) {
      alert("No campaigns selected for deletion or no ESP selected.");
      return;
    }

    const selectedSystem = systems.find(s => s.id === selectedEsp);
    if (!selectedSystem) {
      alert("Unknown ESP system.");
      return;
    }

    setDeletingCampaigns(true);
    setCampaigns([]);  // Clear table while deleting

    try {
      const response = await fetch(
        "https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com/email/delete-campaigns",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            systemId: selectedSystem.id,
            campaigns: deletedCampaigns
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Deletion result:", result);
        setDeletedCampaigns([]);
        await fetchCampaignDataDashboard(); // Refresh campaigns list after deletion
        alert("Selected campaigns deleted successfully.");
      } else {
        console.error("❌ Deletion failed", result);
        alert("Failed to delete campaigns: " + (result?.error || response.status));
      }
    } catch (err) {
      console.error("❌ Network error during deletion:", err);
      alert("Network error during deletion: " + err);
    } finally {
      setDeletingCampaigns(false);
    }
  };

  // Reset the edit campaign form fields
  const resetEditCampaignBox = () => {
    setSelectedSenderId("");
    setSelectedListId("");
    setSelectedIncludedSegmentIds([]);
    setSelectedExcludedSegmentIds([]);
    setSelectedIncludedListIds([]);
    setSelectedExcludedListIds([]);
    setScheduledTime("");
    setHtmlBody("");
    setSelectedCampaign(null);

    // ✅ Clear date/time fields
    setScheduledDay("");
    setScheduledMonth("");
    setScheduledHour("");
    setScheduledMinute("");
    setSelectedGenderValues([]);
  };


  const filteredEsps = systems.filter((s) => s.country === selectedCountry);

  // --- Segment filtering logic for SimplyLoan ---
  const getFilteredSegments = (isInclude: boolean) => {
    if (!selectedEsp || !selectedEsp.startsWith("br_sl_")) {
      return segments;
    }
    const geo = selectedEsp.split("_").pop()?.toUpperCase(); // "SE", "NO", or "FI"
    if (isInclude) {
      return segments.filter(seg => 
        seg.name === `SL${geo}_AG_Fallback` || 
        seg.name === `AG_SendSegment_${geo}`
      );
    } else {
      return segments.filter(seg => seg.name === "Ru_spam_exclusion");
    }
  };

  const filteredIncludedSegments = getFilteredSegments(true);
  const filteredExcludedSegments = getFilteredSegments(false);

  return (
    <MainLayout>
      {/* Header: Title and Country Selector */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">📅 Campaign Scheduler</h1>
        <Select value={selectedCountry} onValueChange={handleCountryChange}>
          <SelectTrigger
            className="w-28 border border-gray-300 shadow-sm rounded-md pl-2 pr-3 py-1.5 text-sm flex items-center justify-start gap-2"
            style={{ minHeight: '36px' }}
          >
            <img
              src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
              alt={selectedCountry}
              className="h-4 w-6 rounded-sm"
            />
            <SelectValue>
              {countries.find((c) => c.id === selectedCountry)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100] w-40 border border-gray-300 rounded-md bg-white shadow-lg">
            {countries.map((c) => (
              <SelectItem
                key={c.id}
                value={c.id}
                className="px-3 py-2 hover:bg-gray-100 text-sm cursor-pointer"
              >
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ESP Selector and Date Range Filters */}
      <div className="mb-1">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          {/* ESP Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ESP</label>
            <select
              className="h-10 w-full border border-gray-300 rounded px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedEsp}
              onChange={(e) => setSelectedEsp(e.target.value)}
            >
              <option value="" disabled>Select ESP</option>
              {filteredEsps.map((esp) => (
                <option key={esp.id} value={esp.id}>{esp.label}</option>
              ))}
            </select>
          </div>
          {/* Date range selectors */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <Input
              type="date"
              className="h-10 w-full border border-gray-300 rounded px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <Input
              type="date"
              className="h-10 w-full border border-gray-300 rounded px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </div>
          {/* Action Buttons */}
          <div className="flex flex-row gap-2 items-end">
            <button
              type="button"
              className="h-10 px-4 border border-gray-300 rounded text-sm bg-white text-gray-800 font-medium transition hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={handleGetData}
            >
              Get Data
            </button>
            <button
              type="button"
              className="h-10 px-4 border border-gray-300 rounded text-sm bg-white text-gray-800 font-medium transition hover:bg-green-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={handleUpdateAllData}
            >
              Update all data
            </button>
          </div>
        </div>
        <div>
          <span className="text-xs text-gray-500 mt-1 block">
            Last updated {lastUpdated}
          </span>
        </div>
      </div>

      {/* Main Grid: Edit Campaign Form & Campaigns Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Edit Campaign Card */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h2 className="text-xl font-semibold mb-2 mt-4">Edit Campaign</h2>

            {/* Sender Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender</label>
              <select
                className="w-full border rounded px-2 py-1 text-sm"
                value={selectedSenderId}
                onChange={e => {
                  setSelectedSenderId(e.target.value);
                  if (selectedCampaign) {
                    setSelectedCampaign({
                      ...selectedCampaign,
                      fromFieldId: e.target.value,
                    });
                  }
                }}
              >
                <option value="">Select sender</option>
                {senders.map(sender => (
                  <option key={sender.fromFieldId} value={sender.fromFieldId}>
                    {sender.name} - {sender.email}
                  </option>
                ))}
              </select>
            </div>

            {/* List Dropdown (Main list for the campaign) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">List</label>
              <div className="flex gap-2">
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={selectedListId}
                  onChange={e => setSelectedListId(e.target.value)}
                >
                  <option value="">Select list</option>
                  {lists.map(list => (
                    <option key={list.campaignId} value={list.campaignId}>
                      {list.name}
                    </option>
                  ))}
                </select>
                {/* Include additional list */}
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-blue-500 text-white text-xs"
                  disabled={!selectedListId || selectedIncludedListIds.includes(selectedListId)}
                  onClick={() => {
                    setSelectedIncludedListIds(ids =>
                      ids.includes(selectedListId) ? ids : [...ids, selectedListId]
                    );
                  }}
                >
                  Include
                </button>
                {/* Exclude additional list */}
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-red-500 text-white text-xs"
                  disabled={!selectedListId || selectedExcludedListIds.includes(selectedListId)}
                  onClick={() => {
                    setSelectedExcludedListIds(ids =>
                      ids.includes(selectedListId) ? ids : [...ids, selectedListId]
                    );
                  }}
                >
                  Exclude
                </button>
              </div>

              {/* Included lists chips (display) */}
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedIncludedListIds.map(listId => {
                  const list = lists.find(l => l.campaignId === listId);
                  return (
                    <span key={listId} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      {list?.name || listId}
                      <button
                        onClick={() => setSelectedIncludedListIds(ids => ids.filter(id => id !== listId))}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ✖
                      </button>
                    </span>
                  );
                })}
              </div>

              {/* Excluded lists chips (display) */}
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedExcludedListIds.map(listId => {
                  const list = lists.find(l => l.campaignId === listId);
                  return (
                    <span key={listId} className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      {list?.name || listId}
                      <button
                        onClick={() => setSelectedExcludedListIds(ids => ids.filter(id => id !== listId))}
                        className="text-red-600 hover:text-red-900"
                      >
                        ✖
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <Input
                value={selectedCampaign?.name || ""}
                onChange={e => {
                  if (!selectedCampaign) return;
                  setSelectedCampaign({
                    ...selectedCampaign,
                    name: e.target.value,
                  });
                }}
              />
            </div>

            {/* Subject Line */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
              <Input
                value={selectedCampaign?.subject || ""}
                onChange={e => {
                  if (!selectedCampaign) return;
                  setSelectedCampaign({
                    ...selectedCampaign,
                    subject: e.target.value,
                  });
                }}
              />
            </div>

            {/* Preview Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preview Text</label>
              <Input
                value={selectedCampaign?.previewText || ""}
                onChange={e => {
                  if (!selectedCampaign) return;
                  setSelectedCampaign({
                    ...selectedCampaign,
                    previewText: e.target.value,
                  });
                }}
              />
            </div>

            {/* Send Time (scheduled date/time inputs) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Send Time</label>
              <div className="flex gap-2 items-end">
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Day</span>
                  <Input
                    ref={dayRef}
                    maxLength={2}
                    value={scheduledDay}
                    placeholder="DD"
                    className="w-14 text-center"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setScheduledDay(val);
                      trySetScheduledTimeFromParts(val, scheduledMonth, scheduledHour, scheduledMinute);
                      if (val.length === 2) monthRef.current?.focus();
                    }}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Month</span>
                  <Input
                    ref={monthRef}
                    maxLength={2}
                    value={scheduledMonth}
                    placeholder="MM"
                    className="w-14 text-center"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setScheduledMonth(val);
                      trySetScheduledTimeFromParts(scheduledDay, val, scheduledHour, scheduledMinute);
                      if (val.length === 2) hourRef.current?.focus();
                    }}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Hour</span>
                  <Input
                    ref={hourRef}
                    maxLength={2}
                    value={scheduledHour}
                    placeholder="HH"
                    className="w-14 text-center"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setScheduledHour(val);
                      trySetScheduledTimeFromParts(scheduledDay, scheduledMonth, val, scheduledMinute);
                      if (val.length === 2) minuteRef.current?.focus();
                    }}
                  />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Minute</span>
                  <Input
                    ref={minuteRef}
                    maxLength={2}
                    value={scheduledMinute}
                    placeholder="MM"
                    className="w-14 text-center"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setScheduledMinute(val);
                      trySetScheduledTimeFromParts(scheduledDay, scheduledMonth, scheduledHour, val);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Include Segments Multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Include Segment(s)</label>
              <select
                multiple
                className="w-full border rounded px-2 py-1 text-sm h-[90px]"
                value={selectedIncludedSegmentIds}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setSelectedIncludedSegmentIds(selected);
                  if (selectedCampaign) {
                    setSelectedCampaign({
                      ...selectedCampaign,
                      includedSegmentIds: selected,
                    });
                  }
                }}
              >
                {filteredIncludedSegments.map(seg => (
                  <option key={seg.searchContactId} value={seg.searchContactId}>
                    {seg.name}
                  </option>
                ))}
              </select>
              {/* Display included segments as chips */}
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedIncludedSegmentIds.map(segId => {
                  const selectedSystem = systems.find(s => s.id === selectedEsp);
                  const seg = segments.find(s => {
                    const sId = String(s.searchContactId);
                    const targetId = String(segId);
                    if (selectedSystem?.esp === "mailerlite") {
                      return String(s.id) === targetId || sId === targetId;
                    }
                    // Fuzzy match for Moosend compound IDs (ListID_SegmentID) when loaded from raw API database
                    return sId === targetId || sId.endsWith(`_${targetId}`);
                  });
                  return (
                    <span key={segId} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      {seg?.name || segId}
                      <button
                        onClick={() =>
                          setSelectedIncludedSegmentIds(prev => prev.filter(sid => sid !== segId))
                        }
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ✖
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Gender Segment — Feedblitz Only */}
            {selectedEsp.startsWith("feedblitz_") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender Segment (M/F/U)</label>
                <div className="flex gap-4 items-center mt-1">
                  {[
                    { value: "M", label: "Male", color: "blue" },
                    { value: "F", label: "Female", color: "pink" },
                    { value: "U", label: "Undefined", color: "gray" },
                  ].map(opt => (
                    <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selectedGenderValues.includes(opt.value)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedGenderValues(prev => [...prev, opt.value]);
                          } else {
                            setSelectedGenderValues(prev => prev.filter(v => v !== opt.value));
                          }
                        }}
                      />
                      <span>{opt.label} ({opt.value})</span>
                    </label>
                  ))}
                </div>
                {selectedGenderValues.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {selectedGenderValues.map(g => (
                      <span key={g} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs font-medium">
                        {g === "M" ? "♂ Male" : g === "F" ? "♀ Female" : "⚪ Undef"}
                      </span>
                    ))}
                    <span className="text-gray-400 text-xs ml-1">→ seg_criteria: {selectedGenderValues.map(g => `Gender=${g}`).join(" OR ")}</span>
                  </div>
                )}
              </div>
            )}

            {/* Exclude Segments Multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exclude Segment(s)</label>
              <select
                multiple
                className="w-full border rounded px-2 py-1 text-sm h-[90px]"
                value={selectedExcludedSegmentIds}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setSelectedExcludedSegmentIds(selected);
                  if (selectedCampaign) {
                    setSelectedCampaign({
                      ...selectedCampaign,
                      excludedSegmentIds: selected,
                    });
                  }
                }}
              >
                {filteredExcludedSegments.map(seg => (
                  <option key={seg.searchContactId} value={seg.searchContactId}>
                    {seg.name}
                  </option>
                ))}
              </select>
              {/* Display excluded segments as chips */}
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedExcludedSegmentIds.map(segId => {
                  const selectedSystem = systems.find(s => s.id === selectedEsp);
                  const seg = segments.find(s => {
                    const sId = String(s.searchContactId);
                    const targetId = String(segId);
                    if (selectedSystem?.esp === "mailerlite") {
                      return String(s.id) === targetId || sId === targetId;
                    }
                    return sId === targetId || sId.endsWith(`_${targetId}`);
                  });
                  return (
                    <span key={segId} className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      {seg?.name || segId}
                      <button
                        onClick={() =>
                          setSelectedExcludedSegmentIds(prev => prev.filter(sid => sid !== segId))
                        }
                        className="text-red-600 hover:text-red-900"
                      >
                        ✖
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Add to Schedule Button */}
            <button
              type="button"
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition"
              disabled={
                !selectedCampaign ||
                (!selectedListId && !(selectedEsp.startsWith("br") || selectedEsp.startsWith("mailerlite"))) ||
                !scheduledTime
              }
              onClick={() => {
                // Ensure the scheduledTime state is up-to-date before using it
                trySetScheduledTimeFromParts(scheduledDay, scheduledMonth, scheduledHour, scheduledMinute);

                // Generate Universal Afima Campaign Tracking ID
                const afimaCampaignId = "AC-" + Math.random().toString(36).substring(2, 10).toUpperCase();

                let safeHtml = htmlBody;
                if (safeHtml) {
                  // Reference from admin.redirect_domains
                    const domainMap: Record<string, string[]> = {
                      'gr_no': ['laninorge.com'],
                      'feedblitz_se': ['onlinelaan.com'],
                      'feedblitz_no': ['onlinetilbud.com'],
                      'feedblitz_fi': ['tarjouksetpostitse.com'],
                      'gr_se': ['sparkredit.se'],
                      'gr_se2': ['svenskalaan.se'],
                      'feedblitz_de': ['kreditoptionen.de', 'gewinnspielen.com'],
                      'gr_de': ['gewinnspielen.com', 'kreditoptionen.de'],
                      'moosend_se': ['swiftkredit.se'],
                      'mailerlite_fi': ['tarjouksesi.com'],
                      'br_fi': ['lainatsuomessa.com'],
                      'gr_fi': ['suomalaisetlainat.com']
                    };
                    
                    // Default fallbacks based on prefix if specific ESP isn't mapped
                    let expectedDomains = domainMap[selectedEsp];
                    if (!expectedDomains) {
                       if (selectedEsp.startsWith('br_sl_')) {
                          if (selectedEsp.endsWith('_se') || selectedCountry === 'SE') expectedDomains = ['simplyloan.se'];
                          else if (selectedEsp.endsWith('_no') || selectedCountry === 'NO') expectedDomains = ['simplyloan.no'];
                          else if (selectedEsp.endsWith('_fi') || selectedCountry === 'FI') expectedDomains = ['simplyloan.fi'];
                          else expectedDomains = ['simplyloan.se', 'simplyloan.no', 'simplyloan.fi'];
                       }
                       else if (selectedEsp.endsWith('_se') || selectedCountry === 'SE') expectedDomains = ['sparkredit.se'];
                       else if (selectedEsp.endsWith('_no') || selectedCountry === 'NO') expectedDomains = ['laninorge.com'];
                       else if (selectedEsp.endsWith('_fi') || selectedCountry === 'FI') expectedDomains = ['suomalaisetlainat.com'];
                       else expectedDomains = ['kreditoptionen.de', 'gewinnspielen.com'];
                    }

                  // Figure out the lead placeholder based on the selected ESP
                  let leadPlaceholder = '{{leadid}}';
                  if (selectedEsp.startsWith('gr_')) leadPlaceholder = '[[leadid]]';
                  else if (selectedEsp.startsWith('feedblitz_')) leadPlaceholder = '<$LeadID$>';
                  else if (selectedEsp.startsWith('br_')) leadPlaceholder = '{{contact.LEADID}}';
                  else if (selectedEsp.startsWith('mailerlite_')) leadPlaceholder = '{$leadid}';
                  else if (selectedEsp.startsWith('moosend_')) leadPlaceholder = '#recipient:LeadID|%20fallback#';

                  // Use the generated universal afimaCampaignId for tracking
                  const sendId = afimaCampaignId;
                  const textVersion = selectedCampaign?.name?.match(/\b[Tt](\d+)\b/)?.[0] || 'T1';

                  try {
                    safeHtml = validateAndInjectTracking(safeHtml, selectedEsp, leadPlaceholder, textVersion, sendId, expectedDomains);
                    
                    // The pure string Gatekeeper specifically for SimplyLoan (The Citroën Model)
                    if (selectedEsp.startsWith('br_sl_')) {
                      safeHtml = scrubSimplyLoanLinks(safeHtml, selectedEsp, afimaCampaignId);
                    }
                  } catch (err: any) {
                    alert(`🚨 TRACKING LINTER FAILED:\n\n${err.message}\n\nPlease fix the links directly in the HTML code before adding to schedule.`);
                    return; // Prevent it from being added to the queue!
                  }
                }

                // After a short delay, add the campaign to the schedule queue using the latest state
                setTimeout(() => {
                  if (selectedCampaign && scheduledTime) {
                    setScheduleQueue(prevQueue => [
                      ...prevQueue,
                      {
                        ...selectedCampaign,
                        afimaCampaignId: afimaCampaignId,
                        date: scheduledTime,
                        fromFieldId: selectedSenderId,
                        campaignId: selectedListId,
                        includedSegmentIds: selectedIncludedSegmentIds,
                        excludedSegmentIds: selectedExcludedSegmentIds,
                        htmlContent: safeHtml, // Store the injected, verified HTML
                        previewText: selectedCampaign.previewText,
                        sendSettings: {
                          selectedCampaigns: [
                            selectedListId,
                            ...selectedIncludedListIds.filter(id => id !== selectedListId)
                          ],
                          excludedCampaigns: selectedExcludedListIds,
                          selectedSegments: selectedIncludedSegmentIds,
                          excludedSegments: selectedExcludedSegmentIds,
                          genderSegment: selectedGenderValues.length > 0 ? selectedGenderValues : undefined,
                        },
                      }
                    ]);
                    resetEditCampaignBox();
                  }
                }, 50);
              }}
            >
              Add to Schedule
            </button>
          </CardContent>
        </Card>

        {/* Campaigns List Card */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h2 className="text-xl font-semibold mb-2 mt-4">Campaigns</h2>
            <div className="overflow-auto max-h-[400px] border rounded">
              <table className="w-full table-auto text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-left px-2 py-1">Delivered to</th>
                    <th className="text-left px-2 py-1">Open Rate</th>
                    <th className="text-left px-2 py-1">Click Rate</th>
                    <th className="text-left px-2 py-1">Unsubs</th>
                    <th className="text-left px-2 py-1">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {refreshingAfterSchedule ? (
                    <tr>
                      <td colSpan={8} className="text-blue-600 text-sm text-center py-4 animate-pulse">
                        🔄 Scheduling & refreshing...
                      </td>
                    </tr>
                  ) : updatingData ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-blue-500 font-semibold animate-pulse">
                        🔄 Updating data... please wait
                      </td>
                    </tr>
                  ) : loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-3">Loading...</td>
                    </tr>
                  ) : campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-3 text-gray-400">No campaigns found.</td>
                    </tr>
                  ) : (
                    campaigns.map((campaign) => (
                      <tr
                        key={campaign.id}
                        onClick={() => handleCampaignClick(campaign)}
                        className={`border-t cursor-pointer ${selectedCampaign?.id === campaign.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
                      >
                        <td className="px-2 py-1">{campaign.name}</td>
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{formatDateCopenhagen(campaign.date)}</td>
                        <td className="px-2 py-1">{campaign.status}</td>
                        <td className="px-2 py-1">{campaign.delivered}</td>
                        <td className="px-2 py-1">{campaign.openRate}</td>
                        <td className="px-2 py-1">{campaign.clickRate}</td>
                        <td className="px-2 py-1">{campaign.unsubs}</td>
                        <td className="px-2 py-1">
                          {(
                            (selectedEsp.startsWith("feedblitz_") && campaign.status?.toLowerCase() !== "sent") ||
                            (!selectedEsp.startsWith("feedblitz_") && (campaign.status?.toLowerCase() === "scheduled" || campaign.status?.toLowerCase() === "draft"))
                          ) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletedCampaigns(prev => [...prev, campaign]);
                                  setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
                                }}
                                className="text-red-500 text-xs ml-2"
                              >
                                ❌
                              </button>
                            )}

                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule Queue Card */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <h2 className="text-lg font-semibold mb-4 mt-4">Campaigns To Be Scheduled</h2>
          {scheduleQueue.length === 0 ? (
            <div className="text-gray-500 text-sm">No campaigns in the queue.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full table-auto text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Subject Line</th>
                    <th className="text-left px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleQueue.map((c, idx) => (
                    <tr key={`${c.id}-${c.date}`}>
                      <td className="px-2 py-1">
                        <input
                          className="w-full border border-gray-300 rounded px-1 text-sm"
                          value={c.name}
                          onChange={(e) => {
                            const updated = [...scheduleQueue];
                            updated[idx].name = e.target.value;
                            setScheduleQueue(updated);
                          }}
                        />
                      </td>
                      <td className="px-2 py-1 font-mono whitespace-nowrap text-sm text-gray-700">
                        {formatDateCopenhagen(c.date)}
                        {c.sendSettings?.genderSegment && c.sendSettings.genderSegment.length > 0 && (
                          <span className="ml-2 bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded font-medium">
                            {c.sendSettings.genderSegment.join("+")}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          className="w-full border border-gray-300 rounded px-1 text-sm"
                          value={c.subject}
                          onChange={(e) => {
                            const updated = [...scheduleQueue];
                            updated[idx].subject = e.target.value;
                            setScheduleQueue(updated);
                          }}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <button
                          className="text-red-600 text-xs px-2 py-1"
                          onClick={() => setScheduleQueue(queue => queue.filter((_, i) => i !== idx))}
                        >
                          ✖ Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {scheduleQueue.length > 0 && (
            <button
              type="button"
              className="mt-4 w-full bg-green-600 text-white px-4 py-2 rounded shadow hover:bg-green-700 transition"
              onClick={handleScheduleAll}
              disabled={scheduling}
            >
              {scheduling ? "Scheduling..." : `Schedule all (${scheduleQueue.length})`}
            </button>
          )}
        </CardContent>
      </Card>

      {/* Deletion Queue Card (if any) */}
      {
        deletedCampaigns.length > 0 && (
          <Card className="mt-6">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold mb-4">Campaigns To Be Deleted</h2>
              <div className="overflow-auto">
                <table className="w-full table-auto text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Subject</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedCampaigns.map((c, idx) => (
                      <tr key={`${c.id}-delete`}>
                        <td>{c.name}</td>
                        <td>{formatDateCopenhagen(c.date)}</td>
                        <td>{c.subject}</td>
                        <td>
                          <button
                            className="text-red-600 text-xs"
                            onClick={() => setDeletedCampaigns(list => list.filter((_, i) => i !== idx))}
                          >
                            ✖ Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {deletingCampaigns && (
                  <div className="text-sm text-red-500 mt-2 animate-pulse">🗑️ Deleting selected campaigns...</div>
                )}
                {deletedCampaigns.length > 0 && !deletingCampaigns && (
                  <button
                    className="mt-4 w-full bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700 transition"
                    onClick={handleDeleteScheduledCampaigns}
                    disabled={deletingCampaigns}
                  >
                    Delete selected ({deletedCampaigns.length})
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )
      }

      {/* HTML Editor Section (shown when a campaign is selected) */}
      {
        selectedCampaign && (
          <Card className="mt-4">
            <details open={htmlEditorOpen} onToggle={e => setHtmlEditorOpen(e.currentTarget.open)}>
              <summary className="cursor-pointer px-4 py-3 border-b text-sm font-medium bg-gray-100">
                HTML Editor {selectedCampaign.name ? `(${selectedCampaign.name})` : ""}
              </summary>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* HTML Source Textarea */}
                  <div>
                    <label htmlFor="htmlBody" className="block text-sm font-medium mb-1">
                      HTML Source
                    </label>
                    <textarea
                      id="htmlBody"
                      ref={htmlTextareaRef}
                      value={htmlBody}
                      onChange={e => setHtmlBody(e.target.value)}
                      className="w-full resize-y border border-gray-300 rounded p-2 font-mono text-sm"
                      style={{ minHeight: 384 }}
                      placeholder="Write HTML here..."
                    />
                  </div>
                  {/* Live Preview */}
                  <iframe
                    title="HTML Preview"
                    srcDoc={htmlBody}
                    style={{
                      width: "100%",
                      height: previewHeight,
                      border: "1px solid #ccc",
                      borderRadius: "0.375rem", // matches Tailwind rounded
                      backgroundColor: "white",
                    }}
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  />
                </div>
              </CardContent>
            </details>
          </Card>
        )
      }


    </MainLayout >
  );
}
